import React, { useState, useCallback, useImperativeHandle } from 'react';

const MOBILE_PROFILE_TAB_BASE =
  'px-3 sm:px-4 py-2 rounded-md font-medium text-xs sm:text-sm transition-colors duration-150 whitespace-nowrap flex-shrink-0';
const MOBILE_PROFILE_TAB_ACTIVE = 'bg-vervestacks-primary text-white';
const MOBILE_PROFILE_TAB_INACTIVE = 'bg-white text-gray-700 hover:bg-gray-50';

/**
 * Mobile/tablet Inputs + System Balance shell. Tab state lives here so switching
 * or closing panels does not re-render the heavy GenerationProfileChart parent.
 */
const MobileSimulatorProfilePanels = React.memo(
  React.forwardRef(function MobileSimulatorProfilePanels(
    { inputsPanel, balancePanel, onLayoutChange },
    ref
  ) {
    const [activeTab, setActiveTab] = useState(null);

    const notifyLayoutAfterPaint = useCallback(() => {
      requestAnimationFrame(() => {
        onLayoutChange?.();
      });
    }, [onLayoutChange]);

    const closePanels = useCallback(() => {
      setActiveTab(null);
      notifyLayoutAfterPaint();
    }, [notifyLayoutAfterPaint]);

    const selectTab = useCallback(
      (tab) => {
        setActiveTab(tab);
        notifyLayoutAfterPaint();
      },
      [notifyLayoutAfterPaint]
    );

    useImperativeHandle(ref, () => ({ closePanels, setActiveTab }), [closePanels]);

    return (
      <div className="lg:hidden w-full min-w-0 flex flex-col gap-2 order-first">
        <div
          className="flex space-x-2 overflow-x-auto scrollbar-hide -mx-2 px-2 sm:mx-0 sm:px-0"
          style={{ WebkitOverflowScrolling: 'touch' }}
          role="tablist"
          aria-label="Simulator panels"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'inputs'}
            onClick={() => selectTab('inputs')}
            className={`${MOBILE_PROFILE_TAB_BASE} ${
              activeTab === 'inputs' ? MOBILE_PROFILE_TAB_ACTIVE : MOBILE_PROFILE_TAB_INACTIVE
            }`}
          >
            Inputs
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'balance'}
            onClick={() => selectTab('balance')}
            className={`${MOBILE_PROFILE_TAB_BASE} ${
              activeTab === 'balance' ? MOBILE_PROFILE_TAB_ACTIVE : MOBILE_PROFILE_TAB_INACTIVE
            }`}
          >
            System Balance
          </button>
        </div>
        <div
          className={`w-full min-w-0 ${activeTab === 'inputs' ? 'block' : 'hidden'}`}
          aria-hidden={activeTab !== 'inputs'}
        >
          {inputsPanel}
        </div>
        <div
          className={`w-full min-w-0 ${activeTab === 'balance' ? 'block' : 'hidden'}`}
          aria-hidden={activeTab !== 'balance'}
        >
          {balancePanel}
        </div>
      </div>
    );
  })
);

export default MobileSimulatorProfilePanels;
